import { useState } from 'react';
import { RESET_PASSWORD_URL, resendVerification } from '../lib/customerAuth';

interface InlineLoginProps {
  onLogin: (email: string, password: string) => Promise<{
    success: boolean;
    error?: string;
    needsVerification?: boolean;
  }>;
  onSwitchToRegister?: () => void;
  onCancel?: () => void;
  compact?: boolean;
}

export default function InlineLogin({
  onLogin,
  onSwitchToRegister,
  onCancel,
  compact = false,
}: InlineLoginProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setNeedsVerification(false);
    setLoading(true);
    const result = await onLogin(email, password);
    setLoading(false);
    if (!result.success) {
      setNeedsVerification(!!result.needsVerification);
      setError(result.error || 'Login failed');
    }
  };

  const handleResend = async () => {
    if (!email) return;
    try {
      await resendVerification(email);
      setError('Verification email resent! Check your inbox.');
      setNeedsVerification(false);
    } catch {
      setError('Failed to resend verification email.');
    }
  };

  return (
    <div className={`inline-auth${compact ? ' inline-auth--compact' : ''}`}>
      <form onSubmit={handleSubmit} className="inline-auth-form">
        <div className="contact-field">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            placeholder="you@example.com"
          />
        </div>
        <div className="contact-field">
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={8}
            autoComplete="current-password"
          />
        </div>

        {error && (
          <p className={`inline-auth-error${needsVerification ? ' inline-auth-error--warn' : ''}`}>
            {error}
            {needsVerification && (
              <button type="button" className="inline-auth-link-btn" onClick={handleResend}>
                Resend verification email
              </button>
            )}
          </p>
        )}

        <div className="inline-auth-actions">
          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </button>
          {onCancel && (
            <button type="button" className="btn btn-secondary" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="inline-auth-links">
        <a href={RESET_PASSWORD_URL} target="_blank" rel="noopener noreferrer">
          Forgot password?
        </a>
        {onSwitchToRegister && (
          <button type="button" className="inline-auth-link-btn" onClick={onSwitchToRegister}>
            Create account
          </button>
        )}
      </div>
    </div>
  );
}
